/**
 * Le CHIFFRAGE montré DANS le bon (ébauche validée par Benjamin le 2026-10-24) : chaque ligne dit si elle est « compris au contrat »
 * ou « à facturer », puis le total à facturer. Sous contrat annuel, les pièces comprises sont regroupées en une rangée
 * (« Pièces du palier B (6) ») et leur montant n'est pas affiché (« — ») : elles ne se facturent pas ; le total compris, pour la
 * rentabilité, est dit sous le tableau. Aucun calcul ici : le serveur est l'autorité, on ne fait que présenter.
 *
 * Logique pure, testée sans navigateur. Réservé à qui voit les prix (la route l'exige).
 */
import type { ChiffrageInterventionVue, LigneChiffreeVue } from '../api/client';
import { montantAffiche, phraseRaison } from './tarifs-mo';

export interface RangeeChiffrageBon {
  readonly cle: string;
  readonly libelle: string;
  /** « compris au contrat » ou « à facturer » ; `null` sur un bon facturé à la visite (tout l'est : la pastille n'apprend rien). */
  readonly pastille: 'compris' | 'a-facturer' | null;
  /** Le montant à afficher : un montant, « — » pour une ligne comprise, ou la raison d'une ligne non chiffrée. */
  readonly montant: string;
  /** Vrai quand la ligne à facturer n'est pas chiffrée : le montant est une raison, à mettre en évidence. */
  readonly manque: boolean;
}

export interface ChiffrageBonVue {
  readonly rangees: readonly RangeeChiffrageBon[];
  /** « 127,00 € » ; préfixé « au moins » quand une ligne à facturer n'est pas chiffrée. */
  readonly totalAFacturer: string;
  /** « Compris au contrat annuel : 399,25 € HT (non facturé) » ; `null` hors contrat annuel et sans rien de compris. */
  readonly phraseCompris: string | null;
}

/** Le libellé d'une ligne : « Courroie plate striée ×1 », « Main d’œuvre en plus, 1 h », « Main d’œuvre — Benjamin, 1 h 45 ». */
export function libelleLigneChiffree(l: LigneChiffreeVue): string {
  const base = l.libelle.replace(/\s*\([^)]*\)\s*$/, '');
  if (l.genre === 'PIECE') return l.quantite ? `${base} ×${l.quantite.replace('.', ',')}` : base;
  if (l.genre === 'MAIN_D_OEUVRE') {
    // Une main d'œuvre EN PLUS a une clé de ligne ; une fois figée elle n'en a plus, mais sa quantité reste un nombre d'heures
    // (« 1.5 »), alors que celle de l'équipe est une durée (« 1 h 45 »).
    if (l.cle !== null || /^\d+(?:[.,]\d+)?$/.test(l.quantite ?? '')) {
      const enPlus = /en plus/i.test(base) ? base : `${base} (en plus)`;
      return l.quantite ? `${enPlus}, ${l.quantite.replace('.', ',')} h` : enPlus;
    }
    return l.quantite ? `${base}, ${l.quantite}` : base;
  }
  if (l.genre === 'DEPLACEMENT' && l.cle !== null && l.quantite && l.quantite !== '1') return `${base} ×${l.quantite.replace('.', ',')}`;
  return base;
}

/**
 * Présente le chiffrage du serveur sous la forme de l'ébauche. `palier` : le code du palier d'où viennent les pièces (« B »), lu sur
 * les lignes versées du bon ; absent (bon validé, ou plusieurs paliers), la rangée dit « Pièces de la gamme ».
 */
export function chiffrageDuBon(c: ChiffrageInterventionVue, palier?: string | null): ChiffrageBonVue {
  const contrat = c.facturationBon === 'CONTRACT';
  const rangees: RangeeChiffrageBon[] = [];
  const piecesComprises = c.lignes.filter((l) => l.genre === 'PIECE' && l.facturation === 'COMPRIS');
  // Les lignes portées par une autre intervention du passage (compte = faux, sans montant) n'ont rien à dire dans le bon.
  const visibles = c.lignes.filter((l) => l.compte || l.total !== null);

  let piecesPosees = false;
  visibles.forEach((l, i) => {
    if (l.genre === 'PIECE' && l.facturation === 'COMPRIS') {
      if (piecesPosees) return;
      piecesPosees = true;
      rangees.push({
        cle: 'pieces-comprises',
        libelle: palier ? `Pièces du palier ${palier} (${piecesComprises.length})` : `Pièces de la gamme (${piecesComprises.length})`,
        pastille: 'compris',
        montant: '—',
        manque: false,
      });
      return;
    }
    const compris = l.facturation === 'COMPRIS';
    const manque = !compris && l.total === null && l.compte;
    rangees.push({
      cle: `${l.genre}-${l.cle ?? 'auto'}-${i}`,
      libelle: libelleLigneChiffree(l),
      pastille: contrat ? (compris ? 'compris' : 'a-facturer') : null,
      montant: compris ? '—' : l.total !== null ? montantAffiche(l.total) : phraseRaison(l.raison) || 'Non chiffré',
      manque,
    });
  });

  const total = montantAffiche(c.totalHt);
  const compris = Number(c.totalComprisHt);
  const comprisNonChiffre = c.lignes.some((l) => l.facturation === 'COMPRIS' && l.compte && l.total === null);
  return {
    rangees,
    totalAFacturer: c.incomplet ? `au moins ${total}` : total,
    phraseCompris:
      contrat || (Number.isFinite(compris) && compris !== 0)
        ? `Compris au contrat annuel : ${comprisNonChiffre ? 'au moins ' : ''}${montantAffiche(c.totalComprisHt)} HT (non facturé, pour la rentabilité)`
        : null,
  };
}
